import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SafeImage } from '@/components/ui/safe-image';
import { LayoutGrid, List, Loader2, MapPin, Search } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { MediaAvailability, MediaLocation, StoreProfileDetail } from '../../types';
import { MediaAvailabilityBadge } from '../MediaAvailabilityBadge';
import { ProvenanceBadge } from '../ProvenanceBadge';

interface MediaInventoryTabProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
}

type MappingFilter = 'all' | 'placed' | 'unplaced';

function isPlaced(media: MediaLocation): boolean {
  return Boolean(media.floorplanAisleId || media.floorplanZoneId);
}

export function MediaInventoryTab({ profile, storeId, storeIdParam }: MediaInventoryTabProps) {
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [search, setSearch] = useState('');
  const [formatFilter, setFormatFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState<MediaAvailability | 'all'>('all');
  const [mappingFilter, setMappingFilter] = useState<MappingFilter>('all');
  const [pendingMediaId, setPendingMediaId] = useState<string | null>(null);
  const [assignTargets, setAssignTargets] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canAssign = storeId != null && storeIdParam != null;

  const formats = [...new Set(profile.mediaLocations.map((m) => m.format))];
  const aisleLabelById = new Map(profile.aisles.map((a) => [a.id, `Aisle ${a.number} — ${a.name}`]));
  const zoneLabelById = new Map(profile.floorplanZones.map((z) => [z.id, z.label]));
  const placedCount = profile.mediaLocations.filter(isPlaced).length;

  const filtered = profile.mediaLocations.filter((m) => {
    if (formatFilter !== 'all' && m.format !== formatFilter) return false;
    if (availabilityFilter !== 'all' && m.availability !== availabilityFilter) return false;
    if (mappingFilter === 'placed' && !isPlaced(m)) return false;
    if (mappingFilter === 'unplaced' && isPlaced(m)) return false;
    if (search) {
      const q = search.toLowerCase();
      return m.locationLabel.toLowerCase().includes(q) || m.formatLabel.toLowerCase().includes(q) || m.zone.toLowerCase().includes(q);
    }
    return true;
  });

  const placementLabel = (media: MediaLocation): string | null => {
    if (media.floorplanAisleId) return aisleLabelById.get(media.floorplanAisleId) ?? 'Aisle';
    if (media.floorplanZoneId) return zoneLabelById.get(media.floorplanZoneId) ?? 'Zone';
    return null;
  };

  const assignSpatialLink = async (media: MediaLocation, targetValue: string) => {
    if (storeId == null || media.liveRequirementId == null) return;
    const [kind, ...rest] = targetValue.split(':');
    const id = rest.join(':');
    setPendingMediaId(media.id);
    try {
      const res = await apiRequest(
        `/api/store-profiles/${storeId}/media-requirements/${media.liveRequirementId}/spatial-link`,
        'PATCH',
        kind === 'aisle'
          ? { floorplanAisleId: id, floorplanZoneId: null }
          : { floorplanAisleId: null, floorplanZoneId: id },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to assign media location');
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/store-profiles/summary'] }),
      ]);
      toast({ title: 'Media location placed', description: `Linked to ${kind === 'aisle' ? 'aisle' : 'zone'}.` });
    } catch (error) {
      toast({
        title: 'Could not assign media location',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setPendingMediaId(null);
    }
  };

  const assignControl = (media: MediaLocation) => {
    if (!canAssign || media.liveRequirementId == null) return null;
    const options = [
      ...profile.aisles.map((a) => ({ value: `aisle:${a.id}`, label: `Aisle ${a.number} — ${a.name}` })),
      ...profile.floorplanZones.map((z) => ({ value: `zone:${z.id}`, label: `Zone: ${z.label}` })),
    ];
    if (options.length === 0) {
      return <p className="text-xs text-gray-500">Define an aisle or zone first.</p>;
    }
    const selected = assignTargets[media.id] ?? '';
    return (
      <div className="flex gap-2">
        <Select
          value={selected}
          onValueChange={(value) => setAssignTargets((current) => ({ ...current, [media.id]: value }))}
        >
          <SelectTrigger className="h-8 text-xs" aria-label={`Assign ${media.formatLabel} to an aisle or zone`}>
            <SelectValue placeholder="Place on floorplan…" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          size="sm"
          className="h-8"
          disabled={!selected || pendingMediaId === media.id}
          onClick={() => void assignSpatialLink(media, selected)}
        >
          {pendingMediaId === media.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : 'Assign'}
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden />
          <Input placeholder="Search locations..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" aria-label="Search media locations" />
        </div>
        <Select value={formatFilter} onValueChange={setFormatFilter}>
          <SelectTrigger className="w-40" aria-label="Filter by format"><SelectValue placeholder="Format" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All formats</SelectItem>
            {formats.map((f) => <SelectItem key={f} value={f}>{f.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={availabilityFilter} onValueChange={(v) => setAvailabilityFilter(v as MediaAvailability | 'all')}>
          <SelectTrigger className="w-40" aria-label="Filter by availability"><SelectValue placeholder="Availability" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="in_campaign">In Campaign</SelectItem>
            <SelectItem value="reserved">Reserved</SelectItem>
            <SelectItem value="unavailable">Unavailable</SelectItem>
            <SelectItem value="damaged">Damaged</SelectItem>
          </SelectContent>
        </Select>
        <Select value={mappingFilter} onValueChange={(v) => setMappingFilter(v as MappingFilter)}>
          <SelectTrigger className="w-40" aria-label="Filter by floorplan placement"><SelectValue placeholder="Placement" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any placement</SelectItem>
            <SelectItem value="placed">Placed on floorplan</SelectItem>
            <SelectItem value="unplaced">Not placed</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-1 ml-auto">
          <Button variant={viewMode === 'cards' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('cards')} aria-label="Card view"><LayoutGrid className="h-4 w-4" /></Button>
          <Button variant={viewMode === 'table' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('table')} aria-label="Table view"><List className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
        <span>
          {filtered.length} of <span className="font-medium text-gray-900">{profile.totalMediaLocations}</span> media locations
          · total capacity <span className="font-medium text-gray-900">{profile.totalMediaCapacity}</span>
        </span>
        <ProvenanceBadge provenance={profile.totalMediaLocations > 0 ? 'live' : 'missing'} />
        {placedCount < profile.totalMediaLocations && (
          <span className="text-amber-700">
            · {profile.totalMediaLocations - placedCount} not placed on floorplan
          </span>
        )}
      </div>

      {viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((media) => {
            const placement = placementLabel(media);
            return (
              <Card key={media.id}>
                {media.verificationImageUrl && (
                  <SafeImage src={media.verificationImageUrl} alt={media.locationLabel} className="w-full h-32 object-cover rounded-t-lg" placeholderType="photo" />
                )}
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{media.formatLabel}</p>
                      <p className="text-sm text-gray-500">{media.locationLabel}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <ProvenanceBadge provenance={media.dataSource === 'live' ? 'live' : 'demo'} />
                      <MediaAvailabilityBadge availability={media.availability} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="outline">{media.zone}</Badge>
                    {placement ? (
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                        <MapPin className="h-3 w-3 mr-1" aria-hidden />{placement}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-amber-50 text-amber-700">Not placed</Badge>
                    )}
                    {media.aisleNumber != null && media.aisleNumber > 0 && <Badge variant="secondary">Aisle {media.aisleNumber}</Badge>}
                  </div>
                  <dl className="text-sm grid grid-cols-2 gap-1">
                    <div><dt className="text-gray-500">Capacity</dt><dd>{media.capacity}</dd></div>
                    <div><dt className="text-gray-500">Dimensions</dt><dd>{media.dimensions}</dd></div>
                    <div><dt className="text-gray-500">Condition</dt><dd className="capitalize">{media.condition}</dd></div>
                    <div><dt className="text-gray-500">Last surveyed</dt><dd>{media.lastSurveyedDate}</dd></div>
                  </dl>
                  {media.currentCampaignName && (
                    <p className="text-sm"><span className="text-gray-500">Campaign:</span> {media.currentCampaignName}</p>
                  )}
                  {media.installationRequirements && (
                    <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">{media.installationRequirements}</p>
                  )}
                  {assignControl(media)}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Format</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Placement</TableHead>
                  <TableHead>Capacity</TableHead>
                  <TableHead>Dimensions</TableHead>
                  <TableHead>Availability</TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Last Surveyed</TableHead>
                  {canAssign && <TableHead>Assign</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((media) => (
                  <TableRow key={media.id}>
                    <TableCell className="font-medium">{media.formatLabel}</TableCell>
                    <TableCell>{media.locationLabel}</TableCell>
                    <TableCell>
                      {placementLabel(media) ?? <span className="text-amber-700">Not placed</span>}
                    </TableCell>
                    <TableCell>{media.capacity}</TableCell>
                    <TableCell>{media.dimensions}</TableCell>
                    <TableCell><MediaAvailabilityBadge availability={media.availability} /></TableCell>
                    <TableCell className="max-w-[150px] truncate">{media.currentCampaignName ?? '—'}</TableCell>
                    <TableCell className="capitalize">{media.condition}</TableCell>
                    <TableCell>{media.lastSurveyedDate}</TableCell>
                    {canAssign && <TableCell className="min-w-[220px]">{assignControl(media)}</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {filtered.length === 0 && (
        <Card><CardContent className="py-12 text-center text-gray-500">No media locations match your filters.</CardContent></Card>
      )}
    </div>
  );
}
